import { Component, lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";

/**
 * SplineRobot — /contact 3D robot aside.
 *
 * Rev 20 Loop B2: scene swapped to "Whobee" (21st.dev community,
 * `PyzDhpQ9E5f1E3MT`). Identical robot model on a square platform
 * with NO embedded "Get in touch" 3D button or LinkedIn click leak.
 * Owner directive: keep ONLY the robot — no NEXIRT-branded pill, no
 * Spline watermark UI overlay. See ADR-0029 amendment.
 *
 * Prior history:
 *   - rev 16 Loop 1: scene activated (`glAwaw5DPnR693L8`, victorgg).
 *   - rev 17 Loop A.5: head-clip fix + NEXIRT pill.
 *   - rev 19 Loop A0: opaque watermark mask + click-strip + square
 *     container. Resolved 3 owner-flagged defects.
 *   - rev 20 Loop B2 (this): scene swap to bare-robot Whobee + drop
 *     attribution UI per owner.
 *
 * CSP allowlist (response-headers policy
 * 3c3ee3ec-b2af-435c-95c9-f4120219730b) unchanged — `prod.spline.
 * design` + Google CDN entries already cover the new scene URL.
 *
 * Lazy-mount strategy unchanged: IntersectionObserver + 2s idle /
 * first interaction; reduced-motion → SVG fallback.
 */

const SPLINE_ENABLED = true;
const SPLINE_SCENE_URL: string | null = "https://prod.spline.design/PyzDhpQ9E5f1E3MT/scene.splinecode";

// Hardened runtime host (SplineSurface): serialised + abortable loads,
// 15 s timeout, pause when off-screen / tab hidden. Recovered from the
// 2026-09-13 production build — see SplineSurface.tsx.
const SplineWrapper = SPLINE_ENABLED
  ? lazy(() => import("./SplineSurface"))
  : null;

/**
 * SplineErrorBoundary — if the Spline runtime throws during mount or
 * render (scene URL revoked, network blip post-load, runtime panic),
 * render the static SVG instead of unmounting the subtree silently.
 * Pairs with the Suspense fallback for the loading window.
 */
class SplineErrorBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err: unknown) { console.error("Spline runtime error; falling back to SVG", err); }
  render() { return this.state.hasError ? this.props.fallback : this.props.children; }
}

function StaticRobot() {
  // Geometric line-art robot. ~1 KB inline; pure SVG; matches the
  // ASCII-HUD brand language (corner brackets, mono accents).
  return (
    <svg
      viewBox="0 0 480 480"
      role="img"
      aria-label="Friendly NEXIRT robot"
      data-spline-fallback
      className="block w-full h-auto"
      style={{ maxWidth: 480 }}
    >
      <defs>
        <linearGradient id="nx-robot-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--nx-accent)" stopOpacity="0.7" />
          <stop offset="1" stopColor="var(--nx-accent-2)" stopOpacity="0.7" />
        </linearGradient>
      </defs>
      <g
        fill="none"
        stroke="url(#nx-robot-grad)"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      >
        {/* Head */}
        <rect x="170" y="120" width="140" height="120" rx="14" />
        {/* Antenna */}
        <line x1="240" y1="120" x2="240" y2="90" />
        <circle cx="240" cy="84" r="5" fill="var(--nx-accent-2)" stroke="none" />
        {/* Eye row */}
        <rect x="195" y="155" width="40" height="18" rx="3" />
        <rect x="245" y="155" width="40" height="18" rx="3" />
        <circle cx="215" cy="164" r="3.5" fill="var(--nx-accent-2)" stroke="none" />
        <circle cx="265" cy="164" r="3.5" fill="var(--nx-accent-2)" stroke="none" />
        {/* Mouth grille */}
        <line x1="200" y1="205" x2="280" y2="205" />
        <line x1="200" y1="215" x2="280" y2="215" />
        <line x1="220" y1="195" x2="220" y2="225" />
        <line x1="240" y1="195" x2="240" y2="225" />
        <line x1="260" y1="195" x2="260" y2="225" />
        {/* Neck */}
        <line x1="220" y1="240" x2="220" y2="260" />
        <line x1="260" y1="240" x2="260" y2="260" />
        {/* Torso */}
        <rect x="160" y="260" width="160" height="130" rx="10" />
        {/* Chest panel */}
        <rect x="200" y="285" width="80" height="50" rx="4" />
        <line x1="215" y1="300" x2="265" y2="300" stroke="var(--nx-accent-2)" strokeOpacity="0.5" />
        <line x1="215" y1="312" x2="245" y2="312" stroke="var(--nx-accent-2)" strokeOpacity="0.5" />
        <line x1="215" y1="324" x2="255" y2="324" stroke="var(--nx-accent-2)" strokeOpacity="0.5" />
        {/* Status dot */}
        <circle cx="285" cy="275" r="4" fill="var(--nx-success)" stroke="none" />
        {/* Arms */}
        <line x1="160" y1="285" x2="120" y2="320" />
        <rect x="105" y="320" width="30" height="60" rx="6" />
        <line x1="320" y1="285" x2="360" y2="320" />
        <rect x="345" y="320" width="30" height="60" rx="6" />
        {/* Legs */}
        <line x1="200" y1="390" x2="200" y2="430" />
        <rect x="185" y="430" width="30" height="14" rx="3" />
        <line x1="280" y1="390" x2="280" y2="430" />
        <rect x="265" y="430" width="30" height="14" rx="3" />
        {/* HUD corner brackets */}
        <path d="M 80 60 L 80 100 M 80 60 L 120 60" />
        <path d="M 400 60 L 400 100 M 400 60 L 360 60" />
        <path d="M 80 460 L 80 420 M 80 460 L 120 460" />
        <path d="M 400 460 L 400 420 M 400 460 L 360 460" />
      </g>
      {/* Coords pip */}
      <text
        x="240"
        y="50"
        fill="var(--nx-text-faint)"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
        fontSize="9"
        letterSpacing="3"
        textAnchor="middle"
      >
        UNIT-001 · ONLINE
      </text>
    </svg>
  );
}

const SPLINE_KEYFRAMES = `
.nx-spline-fadein { animation: nx-spline-in 0.45s ease-out both; }
@keyframes nx-spline-in { from { opacity: 0; } to { opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  .nx-spline-fadein { animation: none; }
}
`;

export default function SplineRobot() {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [shouldMount, setShouldMount] = useState(false);
  const [reduced, setReduced] = useState(false);
  // Save-Data (data-saver / metered connection) gets the static SVG too —
  // the Spline runtime + scene is ~2 MB.
  const [saveData, setSaveData] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setReduced(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
    setSaveData(conn?.saveData === true);
  }, []);

  useEffect(() => {
    if (!SPLINE_ENABLED || reduced || saveData) return;
    if (typeof window === "undefined" || !hostRef.current) return;

    let observer: IntersectionObserver | null = null;
    let idleTimer: number | null = null;
    let interactionListener: ((e: Event) => void) | null = null;

    const trigger = () => {
      setShouldMount(true);
      cleanup();
    };
    const cleanup = () => {
      observer?.disconnect();
      observer = null;
      if (idleTimer != null) {
        clearTimeout(idleTimer);
        idleTimer = null;
      }
      if (interactionListener) {
        window.removeEventListener("pointermove", interactionListener);
        window.removeEventListener("scroll", interactionListener);
        interactionListener = null;
      }
    };

    const onInView = () => {
      // Earlier of: 2 s idle OR first user interaction. Whichever
      // fires first tears down the other.
      idleTimer = window.setTimeout(trigger, 2000);
      interactionListener = () => trigger();
      window.addEventListener("pointermove", interactionListener, { once: true, passive: true });
      window.addEventListener("scroll", interactionListener, { once: true, passive: true });
    };

    if (typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) {
              observer?.disconnect();
              onInView();
              return;
            }
          }
        },
        { rootMargin: "200px" },
      );
      observer.observe(hostRef.current);
    } else {
      onInView();
    }

    return cleanup;
  }, [reduced, saveData]);

  // Rev 19 Loop A0 — owner-escalation hot-fix:
  //   D1 (watermark bleed): pill made fully opaque + 2px outer ring +
  //     opaque mask backplate that covers the in-canvas watermark area.
  //   D2 (LinkedIn click leak): onLoad strips mouseDown/mouseUp/click
  //     listeners from the scene so the "Get in touch" 3D button can't
  //     navigate to the community-scene creator's LinkedIn.
  //   D3 (billboard container): aspect-square + max-w-[480px] centered
  //     so the robot reads as a peer to the form, not a tower.
  return (
    <div
      ref={hostRef}
      className="nx-spline-host relative mx-auto flex w-full max-w-[420px] aspect-[5/6] items-end justify-center overflow-hidden"
      data-spline-host
      data-spline-enabled={SPLINE_ENABLED ? "true" : "false"}
      data-spline-mounted={shouldMount ? "true" : "false"}
    >
      <style>{SPLINE_KEYFRAMES}</style>
      {SPLINE_ENABLED && shouldMount && SPLINE_SCENE_URL && SplineWrapper ? (
        <SplineErrorBoundary fallback={<StaticRobot />}>
          {/* Rev 31 — during the lazy-import + scene-parse window we
              render EMPTY reserved space (not <StaticRobot/>), then fade
              the live scene in. Kills the owner-reported glitchy SVG→3D
              swap. The wrapper holds fixed dims (aspect-[5/6] + overflow-
              hidden) so empty space causes no layout shift. Error path
              (boundary) + reduced-motion path (below) still get the SVG. */}
          <Suspense fallback={<div className="h-full w-full" aria-hidden />}>
            <div className="nx-spline-fadein h-full w-full">
            <SplineWrapper
              scene={SPLINE_SCENE_URL}
              data-spline-canvas
              onLoad={(spline) => {
                // Defect 2 — strip click/hover listeners from the
                // scene so the "Get in touch" 3D button on the robot's
                // torso (baked into the community .splinecode with an
                // Open-URL → LinkedIn event) can no longer navigate.
                // Best-effort: the public API surface varies across
                // runtime versions, so each call is wrapped
                // defensively. If this fails to defang the click in a
                // post-deploy walk, the next step is wrapping the
                // canvas in pointer-events-none (kills cursor
                // reactivity but is definitive).
                try {
                  const events = ["mouseDown", "mouseUp", "mouseHover", "click"] as const;
                  const api = spline as unknown as { removeEventListener?: (ev: string) => void };
                  for (const ev of events) {
                    try { api.removeEventListener?.(ev); } catch { /* per-event noop */ }
                  }
                } catch (err) {
                  console.warn("Spline event-strip failed; canvas clicks may still navigate", err);
                }
              }}
            />
            </div>
          </Suspense>
        </SplineErrorBoundary>
      ) : reduced || saveData ? (
        <StaticRobot />
      ) : null}

      {/* Rev 21 Loop A0-redesign — full-width bottom strip mask.
          Was rev-20's bottom-right corner notch; owner: "continue
          the bottom cut so we have the entire bottom piece cut off
          instead of just the right side of the bottom piece lol."
          Reads as a clean horizontal trim of the platform rather
          than a corner clipping artifact. Still masks the in-canvas
          Spline watermark; still invisible (no logo, no text). */}
      {SPLINE_ENABLED && shouldMount ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-20 bg-nx-bg"
        />
      ) : null}
    </div>
  );
}
